import React, { useEffect, useState } from "react";
import { Box, Button, Card, colors, Grid, Skeleton, Typography, useMediaQuery, useTheme, Chip } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { CheckCircle, Cancel, Download, FilterAlt, Lock, Rsvp as RsvpIcon } from "@mui/icons-material";
import { Tooltip } from "react-tooltip";
import { PieChart } from "@mui/x-charts/PieChart";
import { Gauge, gaugeClasses } from "@mui/x-charts/Gauge";
import { red, green, purple, orange, grey } from "@mui/material/colors";
import MaterialTable from "../../../../Components/MaterialTable";
import { IColumn } from "../../../../Components/MaterialTable/MaterialTable";
import {
	IComment,
	INVITATION_PRICING_PLANS_ENUM,
	INVITATION_RESPONSE_MAP,
	INVITATION_RESPONSE_TYPE
} from "../../../../store/services/invitations/dto/invitations.dto";
import {
	useApproveResponseMutation,
	useDeleteResponseMutation,
	useGetWeddingInvitationStatsQuery,
	useLazyGetManageWeddingInvitationCommentsQuery
} from "../../../../store/services/invitations/invitations.service";
import { useParams, useSearchParams } from "react-router-dom";
import TableFilter from "../../../../Components/MaterialTable/TableFilter";
import TableRowMenu from "../../../../Components/MaterialTable/TableRowMenu";
import ConfirmModal from "../../../../Components/Modals/ConfirmModal";
import { toast } from "react-toastify";
import { notifyError } from "../../../../utils/NotifyError";
import { IError } from "../../../../utils/getErrorMessage";
import { formatDate } from "../../../../utils/formatDate";
import CardsList from "./CardsList";
import { StyleCircle } from "../../../../Components/Inputs/Settings/styles";
import { useAppDispatch, useAppSelector } from "../../../../store";
import { getFullInvitationsState, setOpenPricingModal } from "../../../../store/slices/invitations/invitations.slice";
import { LockedRsvpWrapper, RsvpChartsContainer } from "./styles";
import { UpgradeBtn, WhiteCrown } from "../../styles";
import { trackEvent } from "../../../../utils/analytics";
import { trackMetaEvent } from "../../../../utils/metaPixel";
import { trackTikTokEvent } from "../../../../utils/tiktokPixel";

const LegendDot = ({ color, label, value }: { color: string; label: string; value: number }) => (
	<Box display="flex" alignItems="center" gap={0.75}>
		<Box sx={{ width: 10, height: 10, borderRadius: "50%", background: color, flexShrink: 0 }} />
		<Typography variant="caption" color="textSecondary">
			{label} ({value.toLocaleString()})
		</Typography>
	</Box>
);

const PER_PAGE = 10;
const Rsvp = () => {
	const [page, setPage] = useState(1);
	const params = useParams();
	const id = params.id;
	const [searchParams] = useSearchParams();
	const password = searchParams.get("password");
	const [statusFilter, setStatusFilter] = useState("0");
	const [deleteResponse, setDeleteResponse] = useState<null | IComment>(null);
	const [approveResponse, setApproveResponse] = useState<null | IComment>(null);
	const { invitationPayedData } = useAppSelector(getFullInvitationsState);
	const [fetchComments, { data, isFetching, isLoading }] = useLazyGetManageWeddingInvitationCommentsQuery();
	const [callDeleteResponse, { isLoading: isDeleting }] = useDeleteResponseMutation();
	const [callApproveResponse, { isLoading: isApproving }] = useApproveResponseMutation();
	const { data: stats, isLoading: isStatsLoading } = useGetWeddingInvitationStatsQuery({
		id: id as string,
		password: password as string
	});
	const theme = useTheme();
	const isSmallerScreen = useMediaQuery(theme.breakpoints.down("md"));
	const isXsScreen = useMediaQuery(theme.breakpoints.down("sm"));
	const dispatch = useAppDispatch();

	const views = stats?.total_views || 0;
	const totalResponses = stats?.total_responses || 0;
	const accepted = stats?.accepted_responses || 0;
	const rejected = stats?.rejected_responses || 0;
	const pending = Math.max(0, totalResponses - accepted - rejected);
	const responseRate = views > 0 ? Math.round((totalResponses / views) * 100) : 0;
	const acceptanceRate = totalResponses > 0 ? Math.round((accepted / totalResponses) * 100) : 0;
	const pieWidth = isXsScreen ? 220 : isSmallerScreen ? 280 : 260;

	useEffect(() => {
		fetchComments({ id: id as string, page: 1, perPage: PER_PAGE, status: 0 });
	}, []);

	if (invitationPayedData.type < INVITATION_PRICING_PLANS_ENUM.ESSENTIAL) {
		return (
			<LockedRsvpWrapper>
				<Box className="icon_ring">
					<RsvpIcon />
				</Box>
				<Typography className="views_value">{(data?.totalItems || 0).toLocaleString()}</Typography>
				<Typography className="views_label">Total Responses</Typography>
				<Typography className="teaser_text">
					Upgrade to Essential to unlock RSVP management, guest confirmations, and exportable response lists.
				</Typography>
				<Box className="locked_row">
					{[CheckCircle, Cancel, FilterAlt, Download].map((Icon, i) => (
						<Box key={i} className="locked_stat">
							<Icon />
							<Lock className="lock_badge" />
						</Box>
					))}
				</Box>
				<UpgradeBtn
					variant={"contained"}
					startIcon={<WhiteCrown />}
					style={{ background: "#789DBC" }}
					onClick={() => {
						trackEvent("upgrade_btn", {
							scope: "Invitations",
							description: "Upgrade Button"
						});
						trackMetaEvent("upgrade_btn", { invitation_id: id }, true);
						trackTikTokEvent("upgrade_btn", { invitation_id: id });
						dispatch(setOpenPricingModal(true));
					}}
				>
					Upgrade to Essential
				</UpgradeBtn>
			</LockedRsvpWrapper>
		);
	}

	const tableColumns: Array<IColumn<IComment>> = [
		{
			label: "Name",
			dataKey: "commenter_name",
			Cell: ({ rowData }) => <Box>{rowData.name}</Box>
		},
		{
			label: "Response",
			dataKey: "commenter_response",
			Cell: ({ rowData }) => (
				<Chip
					label={INVITATION_RESPONSE_MAP[rowData.response]}
					color={rowData.response === INVITATION_RESPONSE_TYPE.COMING ? "success" : "error"}
				/>
			)
		},
		{
			label: "Comment",
			dataKey: "commenter_comment",
			Cell: ({ rowData }) => (
				<div style={{ maxWidth: 400 }}>
					<Typography style={{ overflowWrap: "break-word" }}>{rowData.comment ? rowData.comment : "--"}</Typography>
				</div>
			)
		},
		{
			label: "Visibility",
			dataKey: "commenter_visibility",
			Cell: ({ rowData }) => (
				<Box display={"flex"} alignItems={"center"} gap={1}>
					<StyleCircle $color={rowData.is_email_verified || rowData.isApproved ? colors.green[600] : colors.red[600]} />
					<Typography>{rowData.is_email_verified || rowData.isApproved ? "Visible" : "Hidden"}</Typography>
				</Box>
			)
		},
		{
			label: "Email Verified",
			dataKey: "commenter_email_verified",
			Cell: ({ rowData }) => {
				const emailUnverifiedCase = rowData.isApproved ? "Manually approved" : "Unverified";
				return (
					<div>
						<Typography style={{ overflowWrap: "break-word" }}>
							{rowData.is_email_verified ? "Verified" : emailUnverifiedCase}
						</Typography>
					</div>
				);
			}
		},
		{
			label: "Post Date",
			dataKey: "posted_at",
			Cell: ({ rowData }) => <div style={{ maxWidth: 400 }}>{formatDate(rowData.createdAt)}</div>
		},
		{
			label: "",
			dataKey: "menu",
			Cell: ({ rowData }) => {
				return (
					<TableRowMenu
						items={[
							{
								label: "Approve & Show",
								visible: !rowData.is_email_verified && !rowData.isApproved,
								onSelected: () => {
									setApproveResponse(rowData);
								}
							},
							{
								label: "Delete",
								visible: true,
								onSelected: () => {
									setDeleteResponse(rowData);
								}
							}
						]}
					/>
				);
			}
		}
	];
	const handleDeleteResponse = () => {
		deleteResponse &&
			callDeleteResponse({ _id: deleteResponse.invitation_id, comment_id: deleteResponse._id }).then(
				({ data, error }) => {
					if (data) {
						setDeleteResponse(null);
						toast.success("Response has been deleted.", { autoClose: 3000 });
					}
					if (error) {
						notifyError(error as IError);
					}
				}
			);
	};

	const handleApproveResponse = () => {
		approveResponse &&
			callApproveResponse({ _id: approveResponse?.invitation_id, comment_id: approveResponse?._id }).then(
				({ data, error }) => {
					if (data) {
						setApproveResponse(null);
						toast.success("Response has been Approved.", { autoClose: 3000 });
					}
					if (error) {
						notifyError(error as IError);
					}
				}
			);
	};

	const handlePageChange = (page: number) => {
		if (page > 0) {
			setPage(page);
			fetchComments({ id: id as string, page, perPage: PER_PAGE });
		}
	};

	const filters = (
		<TableFilter
			filterLabel={"Status"}
			filterVal={statusFilter}
			options={[
				{
					label: "All",
					value: "0"
				},
				{
					label: INVITATION_RESPONSE_MAP[INVITATION_RESPONSE_TYPE.COMING],
					value: "" + INVITATION_RESPONSE_TYPE.COMING
				},
				{
					label: INVITATION_RESPONSE_MAP[INVITATION_RESPONSE_TYPE.CANT_COME],
					value: "" + INVITATION_RESPONSE_TYPE.CANT_COME
				}
			]}
			onChange={val => {
				setStatusFilter(val);
				fetchComments({ id: id as string, page: 1, perPage: PER_PAGE, status: +val });
			}}
		/>
	);
	const LoadingSkeleton = () => {
		const loadingArray = new Array(6).fill(0);
		return (
			<Box display="flex" style={{ flexFlow: "row wrap" }} gap={2}>
				{loadingArray.map((_, index) => (
					<Box key={index} width={"100%"}>
						<Skeleton variant={"rectangular"} height={"100px"} />
					</Box>
				))}
			</Box>
		);
	};

	const handleRSVPDownload = () => {
		try {
			const token = localStorage.getItem("token");
			const link = document.createElement("a");
			link.href = `${process.env.REACT_APP_BACKEND_API}/download/${id}/rsvp?token=${token}`;
			link.target = "_blank";
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		} catch (err) {
			console.error("Download failed", err);
		}
	};

	return (
		<Box>
			<RsvpChartsContainer mt={1} container spacing={3}>
				{isStatsLoading ? (
					<>
						<Grid size={isSmallerScreen ? 12 : 6}>
							<Skeleton variant="rounded" height={400} />
						</Grid>
						<Grid size={isSmallerScreen ? 12 : 3}>
							<Skeleton variant="rounded" height={400} />
						</Grid>
						<Grid size={isSmallerScreen ? 12 : 3}>
							<Skeleton variant="rounded" height={400} />
						</Grid>
					</>
				) : (
					<>
						{/* ── RSVP Breakdown Donut ── */}
						<Grid size={isSmallerScreen ? 12 : 6}>
							<Card sx={{ p: 3, display: "flex", flexDirection: "column", overflow: "hidden" }}>
								<Typography fontWeight={700} fontSize={16}>
									RSVP Breakdown
								</Typography>
								<Typography variant="caption" color="textSecondary" display="block" mb={2}>
									{totalResponses.toLocaleString()} total responses received
								</Typography>
								<Box
									flex={1}
									display="flex"
									flexDirection="column"
									justifyContent="center"
									alignItems="center"
									sx={{ overflow: "hidden" }}
								>
									<PieChart
										series={[
											{
												innerRadius: 55,
												outerRadius: 100,
												paddingAngle: 3,
												cornerRadius: 4,
												data: [
													{ label: "Accepted", value: accepted, color: green[400] },
													{ label: "Rejected", value: rejected, color: red[400] },
													...(pending > 0 ? [{ label: "Pending", value: pending, color: orange[300] }] : [])
												],
												arcLabel: item => {
													const pct = Math.round((item.value / (totalResponses || 1)) * 100);
													return pct >= 8 ? `${pct}%` : "";
												},
												arcLabelMinAngle: 20,
												highlightScope: { fade: "global", highlight: "item" }
											}
										]}
										width={pieWidth}
										height={220}
										margin={{ top: 10, bottom: 10, left: 10, right: 10 }}
										hideLegend
									/>
								</Box>
								<Box display="flex" gap={2} flexWrap="wrap" justifyContent="center" mt={1.5}>
									<LegendDot color={green[400]} label="Accepted" value={accepted} />
									<LegendDot color={red[400]} label="Rejected" value={rejected} />
									{pending > 0 && <LegendDot color={orange[300]} label="Pending" value={pending} />}
								</Box>
							</Card>
						</Grid>

						{/* ── Response Rate Gauge ── */}
						<Grid size={isSmallerScreen ? 12 : 3}>
							<Card sx={{ p: 3, display: "flex", flexDirection: "column", overflow: "hidden" }}>
								<Typography fontWeight={700} fontSize={16}>
									Response Rate
								</Typography>
								<Typography variant="caption" color="textSecondary" display="block" mb={1}>
									Share of viewers who submitted an RSVP
								</Typography>
								<Box flex={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center">
									<Gauge
										value={responseRate}
										startAngle={-110}
										endAngle={110}
										height={190}
										innerRadius="68%"
										outerRadius="100%"
										text={() => `${responseRate}%`}
										sx={{
											[`& .${gaugeClasses.valueText}`]: { fontSize: 30, fontWeight: 700, fill: purple[600] },
											[`& .${gaugeClasses.valueArc}`]: { fill: purple[400] },
											[`& .${gaugeClasses.referenceArc}`]: { fill: grey[100] }
										}}
									/>
									<Typography variant="caption" color="textSecondary" textAlign="center" mt={-1}>
										{totalResponses.toLocaleString()} of {views.toLocaleString()} viewers responded
									</Typography>
								</Box>
							</Card>
						</Grid>

						{/* ── Acceptance Rate Gauge ── */}
						<Grid size={isSmallerScreen ? 12 : 3}>
							<Card sx={{ p: 3, display: "flex", flexDirection: "column", overflow: "hidden" }}>
								<Typography fontWeight={700} fontSize={16}>
									Acceptance Rate
								</Typography>
								<Typography variant="caption" color="textSecondary" display="block" mb={1}>
									Share of respondents who confirmed attendance
								</Typography>
								<Box flex={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center">
									<Gauge
										value={acceptanceRate}
										startAngle={-110}
										endAngle={110}
										height={190}
										innerRadius="68%"
										outerRadius="100%"
										text={() => `${acceptanceRate}%`}
										sx={{
											[`& .${gaugeClasses.valueText}`]: { fontSize: 30, fontWeight: 700, fill: green[700] },
											[`& .${gaugeClasses.valueArc}`]: { fill: green[400] },
											[`& .${gaugeClasses.referenceArc}`]: { fill: grey[100] }
										}}
									/>
									<Typography variant="caption" color="textSecondary" textAlign="center" mt={-1}>
										{accepted.toLocaleString()} accepted of {totalResponses.toLocaleString()} responses
									</Typography>
								</Box>
							</Card>
						</Grid>
					</>
				)}
			</RsvpChartsContainer>
			{isSmallerScreen ? (
				<Box>
					<Box display={"flex"} alignItems={"center"} justifyContent={"flex-end"}>
						<Button variant={"contained"} onClick={handleRSVPDownload}>
							Download
						</Button>
						{filters}
					</Box>
					{isFetching || isLoading ? (
						<LoadingSkeleton />
					) : (
						<CardsList
							data={data?.items || []}
							totalItems={data?.totalItems || 0}
							page={page}
							perPage={PER_PAGE}
							onPageChange={handlePageChange}
							setDeleteResponse={setDeleteResponse}
							setApproveResponse={setApproveResponse}
						/>
					)}
				</Box>
			) : (
				<MaterialTable
					tableLabel={`RSVP(${data?.totalItems || 0})`}
					columns={tableColumns}
					data={data?.items || []}
					page={page}
					rowsPerPage={PER_PAGE}
					totalItems={data?.totalItems}
					onPageChange={handlePageChange}
					isLoading={isFetching || isLoading}
					filters={
						<Box display={"flex"} alignItems={"center"} gap={1}>
							<Button variant={"contained"} onClick={handleRSVPDownload}>
								Download
							</Button>
							{filters}
						</Box>
					}
				/>
			)}
			{!!deleteResponse && (
				<ConfirmModal
					open={!!deleteResponse}
					title={"Delete image"}
					message={"Are you sure you want to delete this Response?"}
					onConfirm={handleDeleteResponse}
					onClose={() => setDeleteResponse(null)}
					confirmText={"Yes, Delete"}
					cancelText={"No, Cancel"}
					loading={isDeleting}
				/>
			)}{" "}
			{!!approveResponse && (
				<ConfirmModal
					open={!!approveResponse}
					title={"Response Visibility"}
					message={"This will make the response visible even if the email is not verified."}
					onConfirm={handleApproveResponse}
					onClose={() => setApproveResponse(null)}
					confirmText={"Yes, Approve & Show"}
					cancelText={"No, Cancel"}
					loading={isApproving}
				/>
			)}
			<Tooltip id="email_unverified_tooltip" style={{ backgroundColor: "#789DBC", zIndex: 1000, maxWidth: "300px" }} />
		</Box>
	);
};

export default Rsvp;
